import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useHistory, useParams, Redirect } from 'react-router-dom';
import { Form, Input, Button, message, Spin, Modal, Tooltip } from 'antd-v5';
import usePlanAndLDFlag from '../../../../../hooks/usePlanAndLDFlag';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import StandardLayoutV2 from '../../../../Layout/StandardLayoutV2';
import { SettingsContent } from '../../components/SettingsContent';
import AudienceBuilder from '../../../../shared/AudienceBuilder';
import AudiencePreviewModal from '../../../../shared/AudienceBuilder/AudiencePreviewModal';
import EventSessionForm from '../components/EventSessionForm';
import MassMessageList from '../components/MassMessageList';
import {
  useEvent,
  useEventSessions,
  useCreateEvent,
  useUpdateEvent,
  useArchiveEvent,
  useCreateSession,
  useUpdateSession,
  useArchiveSession,
  useReplaceSessionStores,
} from '../../../../../hooks/query/useEvents';
import { useAudienceCount } from '../../../../../hooks/query/useAudiences';
import './styles.scss';

const DEFAULT_AUDIENCE_CONFIG = { version: 1, operator: 'AND', tags: { included: [] } };

const DEBOUNCE_MS = 500;

const hasAudienceFilters = config => {
  if (config?.tags?.included?.length > 0) return true;
  if (config?.tags?.excluded?.length > 0) return true;
  if (config?.stores?.included?.length) return true;
  if (config?.contactMethods?.length) return true;
  if (config?.lastContacted?.mode) return true;
  return false;
};

const AddEditEvent = () => {
  const history = useHistory();
  const { eventId } = useParams();
  const eventsFlag = usePlanAndLDFlag('events');

  // Redirect if feature flag is disabled
  if (!eventsFlag) {
    return <Redirect to="/" />;
  }

  const [form] = Form.useForm();
  const isEditMode = !!eventId;

  // Local state for sessions
  const [sessions, setSessions] = useState([]);
  const [sessionErrors, setSessionErrors] = useState({});
  const originalSessionsRef = useRef([]);

  // Audience state
  const [audienceConfig, setAudienceConfig] = useState(DEFAULT_AUDIENCE_CONFIG);
  const [audienceId, setAudienceId] = useState(null);
  const [audienceCount, setAudienceCount] = useState(0);
  const [isCountPending, setIsCountPending] = useState(false);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);

  // Debounced audience count
  const audienceCountMutation = useAudienceCount();
  const debounceTimerRef = useRef(null);

  const fetchAudienceCount = useCallback(
    config => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (!hasAudienceFilters(config)) {
        setAudienceCount(0);
        setIsCountPending(false);
        return;
      }
      setIsCountPending(true);
      debounceTimerRef.current = setTimeout(() => {
        audienceCountMutation.mutate(config, {
          onSuccess: data => {
            setAudienceCount(data?.total ?? 0);
            setIsCountPending(false);
          },
          onError: () => {
            setIsCountPending(false);
          },
        });
      }, DEBOUNCE_MS);
    },
    [audienceCountMutation],
  );

  // Fetch count whenever audienceConfig changes
  useEffect(() => {
    fetchAudienceCount(audienceConfig);
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, [audienceConfig]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch event data if editing
  const { data: eventData, isLoading: isLoadingEvent } = useEvent({
    eventId,
    useQueryOptions: { enabled: isEditMode },
  });

  const { data: eventSessions, isLoading: isLoadingSessions } = useEventSessions({
    eventId,
    useQueryOptions: { enabled: isEditMode },
  });

  // Mutations
  const createEventMutation = useCreateEvent();
  const updateEventMutation = useUpdateEvent();
  const archiveEventMutation = useArchiveEvent();
  const createSessionMutation = useCreateSession();
  const updateSessionMutation = useUpdateSession();
  const archiveSessionMutation = useArchiveSession();
  const replaceSessionStoresMutation = useReplaceSessionStores();

  // Populate form when editing
  useEffect(() => {
    if (isEditMode && eventData) {
      form.setFieldsValue({
        name: eventData.name,
      });

      // Populate audience from event data
      if (eventData.audience) {
        setAudienceId(eventData.audience.id);
        setAudienceConfig(eventData.audience.configuration || DEFAULT_AUDIENCE_CONFIG);
      }
    }
  }, [eventData, isEditMode, form]);

  // Populate sessions when editing
  useEffect(() => {
    const sessionsArray = eventSessions?.data || eventSessions;
    if (isEditMode && Array.isArray(sessionsArray) && sessionsArray.length > 0) {
      const mapped = sessionsArray.map(session => ({
        id: session.id,
        date: session.sessionDate || session.date,
        startTime: session.startTime || session.start_time,
        endTime: session.endTime || session.end_time,
        storeIds: session.storeIds || session.stores?.map(s => s.id) || [],
      }));
      setSessions(mapped);
      originalSessionsRef.current = mapped.map(s => ({ ...s, storeIds: [...s.storeIds] }));
    }
  }, [eventSessions, isEditMode]);

  const isLoading = isLoadingEvent || isLoadingSessions;
  const isSaving =
    createEventMutation.isLoading ||
    updateEventMutation.isLoading ||
    createSessionMutation.isLoading ||
    updateSessionMutation.isLoading ||
    archiveSessionMutation.isLoading ||
    replaceSessionStoresMutation.isLoading;

  const handleAddSession = () => {
    setSessions(prev => [
      ...prev,
      { id: `temp-${Date.now()}`, date: null, startTime: null, endTime: null, storeIds: [] },
    ]);
  };

  const handleUpdateSession = (index, updatedSession) => {
    setSessions(prev => {
      const newSessions = [...prev];
      newSessions[index] = { ...newSessions[index], ...updatedSession };
      return newSessions;
    });

    if (sessionErrors[index]) {
      setSessionErrors(prev => {
        const newErrors = { ...prev };
        const sessionError = { ...newErrors[index] };

        Object.keys(updatedSession).forEach(key => {
          if (updatedSession[key] !== null && updatedSession[key] !== undefined) {
            if (key === 'storeIds' && updatedSession[key].length > 0) {
              delete sessionError[key];
            } else if (key !== 'storeIds') {
              delete sessionError[key];
            }
          }
        });

        if (Object.keys(sessionError).length === 0) {
          delete newErrors[index];
        } else {
          newErrors[index] = sessionError;
        }

        return newErrors;
      });
    }
  };

  const handleRemoveSession = index => {
    setSessions(prev => prev.filter((_, i) => i !== index));
  };

  const validateSession = session => {
    const errors = {};
    if (!session.date) errors.date = true;
    if (!session.startTime) errors.startTime = true;
    if (!session.endTime) errors.endTime = true;
    if (!session.storeIds || session.storeIds.length === 0) errors.storeIds = true;
    return errors;
  };

  const validateAllSessions = () => {
    const errors = {};
    let hasErrors = false;

    sessions.forEach((session, index) => {
      const sessionValidationErrors = validateSession(session);
      if (Object.keys(sessionValidationErrors).length > 0) {
        errors[index] = sessionValidationErrors;
        hasErrors = true;
      }
    });

    setSessionErrors(errors);
    return !hasErrors;
  };

  const handleCancel = () => {
    if (isEditMode) {
      history.push(`/Events/event-details/${eventId}`);
    } else {
      history.push('/Events');
    }
  };

  const handleArchive = () => {
    Modal.confirm({
      title: 'Archive Event',
      content: 'Are you sure you want to permanently archive this event?',
      okText: 'Archive',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await archiveEventMutation.mutateAsync(eventId);
          message.success('Event archived successfully');
          history.push('/Events');
        } catch (error) {
          console.error('Error archiving event:', error);
          message.error('Failed to archive event. Please try again.');
        }
      },
    });
  };

  const persistSessionChanges = async () => {
    const originals = originalSessionsRef.current;
    const currentIds = new Set(sessions.map(s => s.id));
    const originalIds = new Set(originals.map(s => s.id));

    const normalizeTime = t => (t ? t.slice(0, 5) : '');

    const removedSessions = originals.filter(s => !currentIds.has(s.id));
    const addedSessions = sessions.filter(s => String(s.id).startsWith('temp-'));
    const potentiallyUpdated = sessions.filter(s => originalIds.has(s.id));

    const archivePromises = removedSessions.map(s =>
      archiveSessionMutation.mutateAsync({ eventId, sessionId: s.id }),
    );

    const updatePromises = [];
    const storeUpdatePromises = [];

    potentiallyUpdated.forEach(current => {
      const original = originals.find(o => o.id === current.id);
      if (!original) return;

      const changes = {};
      if (current.date !== original.date) changes.sessionDate = current.date;
      if (normalizeTime(current.startTime) !== normalizeTime(original.startTime))
        changes.startTime = current.startTime;
      if (normalizeTime(current.endTime) !== normalizeTime(original.endTime)) changes.endTime = current.endTime;

      if (Object.keys(changes).length > 0) {
        updatePromises.push(
          updateSessionMutation.mutateAsync({ eventId, sessionId: current.id, data: changes }),
        );
      }

      const currentStoresSorted = [...(current.storeIds || [])].map(String).sort();
      const originalStoresSorted = [...(original.storeIds || [])].map(String).sort();
      const storesChanged =
        currentStoresSorted.length !== originalStoresSorted.length ||
        currentStoresSorted.some((id, i) => id !== originalStoresSorted[i]);

      if (storesChanged) {
        storeUpdatePromises.push(
          replaceSessionStoresMutation.mutateAsync({
            eventId,
            sessionId: current.id,
            storeIds: current.storeIds || [],
          }),
        );
      }
    });

    const createPromises = addedSessions.map(async s => {
      const result = await createSessionMutation.mutateAsync({
        eventId,
        data: {
          sessionDate: s.date,
          startTime: s.startTime,
          endTime: s.endTime,
        },
      });
      const newSessionId = result?.id || result?.data?.id;
      if (newSessionId && s.storeIds?.length > 0) {
        await replaceSessionStoresMutation.mutateAsync({
          eventId,
          sessionId: newSessionId,
          storeIds: s.storeIds,
        });
      }
    });

    await Promise.all([...archivePromises, ...updatePromises, ...storeUpdatePromises, ...createPromises]);
  };

  const handleSubmit = async values => {
    try {
      if (sessions.length === 0) {
        message.error('Please add at least one event date and time');
        return;
      }

      if (!validateAllSessions()) {
        message.error('Please complete all session fields (Date, Start Time, End Time, and Store)');
        return;
      }

      if (!hasAudienceFilters(audienceConfig)) {
        message.error('Please define at least one audience filter (tags or other filters)');
        return;
      }

      if (isEditMode) {
        await updateEventMutation.mutateAsync({
          eventId,
          data: {
            name: values.name,
            audience: { id: audienceId, configuration: audienceConfig },
          },
        });
        await persistSessionChanges();
      } else {
        const eventPayload = {
          name: values.name,
          sessions: sessions.map(s => ({
            sessionDate: s.date,
            startTime: s.startTime,
            endTime: s.endTime,
            storeIds: s.storeIds || [],
          })),
          audience: { configuration: audienceConfig },
        };
        await createEventMutation.mutateAsync(eventPayload);
      }

      message.success(isEditMode ? 'Event updated successfully' : 'Event created successfully');
      if (isEditMode) {
        history.push(`/Events/event-details/${eventId}`);
      } else {
        history.push('/Events');
      }
    } catch (error) {
      console.error('Error saving event:', error);
      message.error('Failed to save event. Please try again.');
    }
  };

  const breadcrumb = [{ path: '/Events', title: 'Events' }, { title: isEditMode ? 'Edit Event' : 'New Event' }];

  const headerActions = (
    <div className="add-edit-event-header-actions">
      {isEditMode && (
        <Button danger onClick={handleArchive} loading={archiveEventMutation.isLoading}>
          Archive
        </Button>
      )}
      <Tooltip title={audienceCount === 0 ? 'Add at least one audience filter before saving' : ''}>
        <span>
          <Button type="primary" onClick={() => form.submit()} loading={isSaving} disabled={audienceCount === 0}>
            Save
          </Button>
        </span>
      </Tooltip>
      <Button onClick={handleCancel}>Cancel</Button>
    </div>
  );

  if (isEditMode && isLoading) {
    return (
      <StandardLayoutV2
        title={isEditMode ? 'Edit Event' : 'New Event'}
        breadcrumb={breadcrumb}
        classProps="flex-col-left"
        maxWidth="920px"
        styleProps={{ justifyContent: 'flex-start' }}
        containerStyles={{ backgroundColor: 'transparent', paddingRight: '32px', width: '100%' }}
      >
        <div className="loading-container">
          <Spin size="large" />
        </div>
      </StandardLayoutV2>
    );
  }

  return (
    <StandardLayoutV2
      title={isEditMode ? 'Edit Event' : 'New Event'}
      breadcrumb={breadcrumb}
      classProps="flex-col-left"
      maxWidth="920px"
      styleProps={{ justifyContent: 'flex-start' }}
      containerStyles={{ backgroundColor: 'transparent', paddingRight: '32px', width: '100%' }}
      actions={headerActions}
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit} style={{ width: '100%' }}>
        <div className="audience-section-row">
          <div className="audience-section-main">
            {/* Event Name */}
            <Form.Item
              name="name"
              label={<span>Event Name <span style={{ fontWeight: 400, color: 'rgba(0,0,0,0.45)' }}>(required)</span></span>}
              rules={[{ required: true, message: 'Please enter an event name' }]}
              style={{ marginBottom: 0, width: 550 }}
              required={false}
            >
              <Input
                placeholder="Enter an Event Name"
                maxLength={60}
                showCount
                style={{ width: '100%' }}
                data-testid="events__add-modal--name-input"
              />
            </Form.Item>

            <div style={{ margin: '40px 0' }} />

            {/* Audience Section */}
            <SettingsContent
              icon={<FontAwesomeIcon icon={['far', 'users']} />}
              title={
                <span className="audience-title-row">
                  AUDIENCE{' '}
                  <span className="audience-count">
                    {isCountPending || audienceCountMutation.isLoading ? (
                      <Spin size="small" />
                    ) : (
                      <>{audienceCount.toLocaleString()} clients</>
                    )}
                  </span>
                </span>
              }
              description="Define the target audience for this event using tags and filters."
            >
              <AudienceBuilder
                configuration={audienceConfig}
                onChange={setAudienceConfig}
                audienceCount={audienceCount}
                isCountLoading={isCountPending || audienceCountMutation.isLoading}
              />
            </SettingsContent>
          </div>

          <div className="audience-section-sidebar">
            <div className="audience-sidebar-card">
              <div className="audience-sidebar-card__title">
                {isCountPending || audienceCountMutation.isLoading ? (
                  <Spin size="small" />
                ) : (
                  <>${(audienceCount * 0.07).toFixed(2)} estimated cost</>
                )}
              </div>
              <div className="audience-sidebar-card__description">
                The total of all messages for this event have an estimated cost of ${(audienceCount * 0.07).toFixed(2)} if you exceed your plan limit
              </div>
            </div>

            <div className="audience-sidebar-card">
              <div className="audience-sidebar-card__title">
                {isCountPending || audienceCountMutation.isLoading ? (
                  <Spin size="small" />
                ) : (
                  <>{audienceCount.toLocaleString()} Clients</>
                )}
              </div>
              <div className="audience-sidebar-card__description">
                This list updates as client data changes and excludes those who can&apos;t receive messages.
              </div>
              <Tooltip
                title={audienceCount > 5000 ? 'Refine your filters to target fewer than 5,000 clients to preview the list' : ''}
              >
                <Button
                  className="audience-sidebar-card__button"
                  onClick={() => setPreviewModalOpen(true)}
                  disabled={!audienceCount || audienceCount > 5000}
                >
                  View all Clients in Audience
                </Button>
              </Tooltip>
            </div>
          </div>
        </div>

        <AudiencePreviewModal
          open={previewModalOpen}
          onClose={() => setPreviewModalOpen(false)}
          configuration={audienceConfig}
          total={audienceCount}
        />

        <div style={{ margin: '40px 0' }} />

        {/* Event Dates and Times Section */}
        <SettingsContent
          icon={<FontAwesomeIcon icon={['far', 'calendar']} />}
          title="EVENT DATES AND TIMES"
          description="Set up where and when clients can book appointments for this event."
          className="full-width-section"
          contentClassName="w-100-P"
          action={
            <Button
              onClick={handleAddSession}
              icon={<FontAwesomeIcon icon={['fal', 'plus']} />}
            >
              Add Date
            </Button>
          }
        >
          {sessions.length > 0 &&
            sessions.map((session, index) => (
              <EventSessionForm
                key={session.id}
                session={session}
                index={index}
                onUpdate={data => handleUpdateSession(index, data)}
                onRemove={() => handleRemoveSession(index)}
                canRemove
                errors={sessionErrors[index] || {}}
              />
            ))}
        </SettingsContent>

        {isEditMode && (
          <>
            <div style={{ margin: '40px 0' }} />
            <MassMessageList eventId={eventId} audienceId={audienceId} audienceCount={audienceCount} />
          </>
        )}
      </Form>
    </StandardLayoutV2>
  );
};

export default AddEditEvent;
